import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy, TrendingUp, Target, Award, ArrowUpRight, Flame, Zap, PlusCircle,
} from "lucide-react";
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DISMISSAL_COLORS = ["#FF3B30", "#007AFF", "#FF9500", "#34C759", "#AF52DE", "#FFCC00"];

export default function Dashboard({ onOpenLogModal }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data: full } = await api.get("/analytics/full");
        setData(full);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return <div className="text-center text-[#A3A3A3] py-20 text-sm font-mono">Loading dashboard...</div>;

  const s = data?.summary || {};
  const trends = data?.trends || [];
  const dismissals = (data?.dismissals || []).map((d, i) => ({
    ...d, color: DISMISSAL_COLORS[i % DISMISSAL_COLORS.length],
  }));
  const phases = data?.phases || { powerplay: 0, middle: 0, death: 0 };
  const noData = (s.total_matches || 0) === 0;

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* HERO */}
      <div className="relative rounded-2xl overflow-hidden border border-[#262626] bg-gradient-to-r from-[#141414] to-[#1A1A1A] p-8 flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
        <div
          className="absolute inset-0 opacity-10 bg-cover bg-center pointer-events-none"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1730739463889-34c7279277a9?crop=entropy&cs=srgb&fm=jpg&q=85')` }}
        />
        <div className="relative z-10 space-y-3 max-w-xl">
          <span className="bg-[#FF3B30]/20 text-[#FF3B30] text-xs font-mono font-bold px-3 py-1 rounded border border-[#FF3B30]/30 uppercase tracking-widest">
            Season 2026 • Tier 1 Elite
          </span>
          <h2 className="text-4xl md:text-5xl font-black uppercase text-white tracking-tight">
            Hello <span className="text-[#FF3B30]">{user?.name?.split(" ")[0] || "Player"}</span> — <br />
            Performance Command Center
          </h2>
          <p className="text-[#A3A3A3] text-sm md:text-base leading-relaxed">
            Real-time analytics tracking batting aggregates, bowling economy, phase-wise strike
            rates and AI-driven weakness detection for emerging professionals.
          </p>
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              data-testid="hero-view-ai-btn"
              onClick={() => navigate("/ai-coach")}
              className="bg-white text-black hover:bg-slate-200 font-bold px-6 py-2.5 rounded text-sm transition-all flex items-center gap-2 shadow-lg"
            >
              <Zap className="w-4 h-4 text-[#FF3B30]" /> View AI Coach Report
            </button>
            <button
              data-testid="hero-log-match-btn"
              onClick={onOpenLogModal}
              className="bg-[#FF3B30] hover:bg-[#E03229] text-white font-bold px-6 py-2.5 rounded text-sm transition-all flex items-center gap-2 shadow-lg"
            >
              <PlusCircle className="w-4 h-4" /> Log Match
            </button>
          </div>
        </div>

        <div className="relative z-10 bg-[#0A0A0A]/90 border border-[#262626] rounded-xl p-6 w-full md:w-80 space-y-4 shadow-xl">
          <div className="flex items-center justify-between border-b border-[#262626] pb-3">
            <span className="text-xs text-[#A3A3A3] font-mono uppercase">Form Rating</span>
            <span className="text-emerald-400 font-bold text-xs flex items-center gap-1">
              <Flame className="w-3.5 h-3.5" /> {s.consistency_score ?? 0}
            </span>
          </div>
          <StatRow label="Batting Average" value={s.batting_average ?? 0} />
          <StatRow label="Strike Rate" value={s.strike_rate ?? 0} accent="emerald" />
          <StatRow label="Bowling Econ" value={s.bowling_economy ?? 0} />
          <StatRow label="Best Score" value={s.best_score ?? 0} accent="red" />
        </div>
      </div>

      {/* STATS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard testid="stat-card-matches" label="Total Matches" value={s.total_matches} icon={Trophy} accent="#FF3B30" />
        <StatCard testid="stat-card-runs" label="Total Runs" value={s.total_runs} icon={TrendingUp} accent="#34C759" delta="Career aggregate" />
        <StatCard testid="stat-card-wickets" label="Wickets Taken" value={s.total_wickets} icon={Target} accent="#007AFF" delta={`Econ: ${s.bowling_economy}`} />
        <StatCard testid="stat-card-best" label="Best Score" value={s.best_score} icon={Award} accent="#FFCC00" delta="Single innings high" />
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold uppercase text-white">Batting Form & Runs Trend</h3>
              <p className="text-xs text-[#A3A3A3]">Match-by-match run progression</p>
            </div>
            <span className="text-xs font-mono bg-[#262626] text-white px-2.5 py-1 rounded">
              {trends.length} Match{trends.length !== 1 ? "es" : ""}
            </span>
          </div>
          <div className="h-72 w-full">
            {trends.length === 0 ? (
              <EmptyChart msg="Log matches to see run trends" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends}>
                  <XAxis dataKey="match" stroke="#737373" fontSize={12} tickLine={false} />
                  <YAxis stroke="#737373" fontSize={12} tickLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: "#141414", borderColor: "#262626", borderRadius: 8, color: "#fff" }} />
                  <Line type="monotone" dataKey="runs" stroke="#FF3B30" strokeWidth={3} dot={{ fill: "#FF3B30", r: 5 }} activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-6 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-bold uppercase text-white">Dismissal Patterns</h3>
            <p className="text-xs text-[#A3A3A3]">Vulnerability by dismissal type</p>
          </div>
          <div className="h-56 w-full flex items-center justify-center">
            {dismissals.length === 0 ? (
              <EmptyChart msg="No dismissals recorded" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={dismissals} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                    {dismissals.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "#141414", borderColor: "#262626", borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="flex flex-wrap gap-2 justify-center pt-2">
            {dismissals.map((d) => (
              <span key={d.name} className="text-xs flex items-center gap-1.5 text-[#A3A3A3]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                {d.name} ({d.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* PHASES */}
      <div className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold uppercase text-white">Phase-Wise Batting Breakdown</h3>
            <p className="text-xs text-[#A3A3A3]">Runs distribution: Powerplay • Middle • Death overs</p>
          </div>
          <span className="text-xs bg-[#262626] text-white px-3 py-1 rounded font-mono">Aggregate</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PhaseCard label="Powerplay (1-6)" runs={phases.powerplay} accent="#007AFF" note="Aggressive intent in first six overs." />
          <PhaseCard label="Middle Overs (7-15)" runs={phases.middle} accent="#F59E0B" note="Strike rotation against spin & seam." />
          <PhaseCard label="Death Overs (16-20)" runs={phases.death} accent="#FF3B30" note="Finishing power & boundary hitting." />
        </div>
      </div>

      {noData && (
        <div className="text-center text-sm text-[#A3A3A3] bg-[#141414] border border-dashed border-[#262626] rounded-xl p-8">
          No matches logged yet.
          <button onClick={onOpenLogModal} className="ml-2 text-[#FF3B30] font-bold hover:underline" data-testid="empty-log-cta">
            Log your first match →
          </button>
        </div>
      )}
    </div>
  );
}

function StatRow({ label, value, accent }) {
  const color = accent === "emerald" ? "text-emerald-400" : accent === "red" ? "text-[#FF3B30]" : "text-white";
  return (
    <div className="flex justify-between text-sm">
      <span className="text-[#A3A3A3]">{label}</span>
      <span className={`font-bold ${color}`}>{value}</span>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, delta, testid }) {
  return (
    <div data-testid={testid} className="bg-[#141414] border border-[#262626] rounded-xl p-6 space-y-2 hover:border-[#FF3B30]/50 transition-all">
      <div className="flex items-center justify-between text-[#A3A3A3]">
        <span className="text-xs uppercase font-mono tracking-wider">{label}</span>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div className="text-4xl font-black text-white">{value ?? 0}</div>
      {delta && (
        <div className="text-xs text-emerald-400 font-medium flex items-center gap-1">
          <ArrowUpRight className="w-3.5 h-3.5" /> {delta}
        </div>
      )}
    </div>
  );
}

function PhaseCard({ label, runs, accent, note }) {
  return (
    <div className="bg-[#1A1A1A] border border-[#262626] rounded-xl p-5 space-y-3 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-xl pointer-events-none" style={{ backgroundColor: `${accent}0f` }} />
      <span className="text-xs font-mono uppercase tracking-widest font-bold" style={{ color: accent }}>{label}</span>
      <div className="text-3xl font-black text-white">
        {runs} <span className="text-sm font-normal text-[#A3A3A3]">runs</span>
      </div>
      <p className="text-xs text-[#A3A3A3] leading-relaxed">{note}</p>
    </div>
  );
}

function EmptyChart({ msg }) {
  return <div className="w-full h-full flex items-center justify-center text-xs text-[#737373] font-mono uppercase">{msg}</div>;
}
